import { Box, Typography, Divider } from "@mui/material";
import { ClaimTrackingStyles } from "./ClaimTrackingStyles";
import HeadingText from "../../common/HeadingText/HeadingText";
import ClaimTrackingCard from "../ClaimTrakingCard/ClaimTrackingCard";
import BasicTimeline from "../../common/BasicTimeLine/BasicTimeLine";
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import {
  IClaim,
  IClaimTransaction,
} from "../../models/user/dashboard/Dashboard";
import moment from "moment";
import { dateFormat } from "../../constants/Constants";
import { useTranslation } from "react-i18next";

interface ClaimTrackingProps {
  carrierClaimStatus: string;
  initiatedDate: string | null;
  carrierPolicyNo: string;
  claimDescription: string;
  updateDate: string;
  claim: IClaim;
  claimTransactions: IClaimTransaction[];
}

const ClaimTracking: React.FC<ClaimTrackingProps> = ({
  claim,
  claimTransactions,
  carrierClaimStatus,
  initiatedDate,
  carrierPolicyNo,
  claimDescription,
  updateDate,
}) => {
  const { t } = useTranslation();

  const details = [
    {
      label: t("Updated On"),
      value: moment(updateDate).format(dateFormat),
      icon: "calendar",
    },
    {
      label: t("Stage"),
      value: carrierClaimStatus,
      isVerification: true,
    },
    {
      label: t("Description"),
      value: claimDescription,
      icon: "description",
    },
    {
      label: t("Claim Initiated"),
      value: moment(initiatedDate).format(dateFormat),
      icon: "calendar",
    },
    
    {
      label: t("Policy Number"),
      value: carrierPolicyNo,
      icon: "hash",
    },
    
  ];

  const mappedTimelineData = claimTransactions.map((item) => ({
    left: moment(item.transactionDate).format(dateFormat),
    right: (
      <Box sx={ClaimTrackingStyles.timeLineRightContent}>
        <ClaimTrackingCard
          title={item.transactionDescription}
          subTitle={item.payToName || ""}
        />
      </Box>
    ),
  }));

  return (
    <Box 
      id="claim-tracking-container"
      data-test-id="test-claim-tracking-container"
      aria-label="Claim tracking container"
      sx={ClaimTrackingStyles.container}
    >
      {/* Claim Details Section */}
      <Box
        id="claim-tracking-details-section"
        data-test-id="test-claim-tracking-details-section"
        aria-label="Claim details section"
      >
        <Typography 
          id="claim-tracking-heading"
          data-test-id="test-claim-tracking-heading"
          aria-label="Claim details heading"
          variant="h6" 
          sx={ClaimTrackingStyles.headingText}
        >
          {t("Claim Details")}
        </Typography>
        <Box 
          id="claim-tracking-details-container"
          data-test-id="test-claim-tracking-details-container"
          aria-label="Claim details content container"
          sx={ClaimTrackingStyles.detailsContainer}
        >
          <Box 
            id="claim-tracking-details-grid"
            data-test-id="test-claim-tracking-details-grid"
            aria-label="Claim details grid"
            sx={ClaimTrackingStyles.detailsGrid}
          >
            {details.map((detail, index) => (
              <Box 
                key={index} 
                id={`claim-tracking-detail-item-${index}`}
                data-test-id={`test-claim-tracking-detail-item-${index}`}
                aria-label={`Claim detail item ${index + 1}`}
                sx={ClaimTrackingStyles.detailItem}
              >
                <Typography 
                  id={`claim-tracking-detail-label-${index}`}
                  data-test-id={`test-claim-tracking-detail-label-${index}`}
                  aria-label={`${detail.label} label`}
                  sx={ClaimTrackingStyles.detailLabel}
                >
                  {detail.label}
                </Typography>
                {detail.isVerification ? (
                  <Box 
                    id={`claim-tracking-verification-badge-${index}`}
                    data-test-id={`test-claim-tracking-verification-badge-${index}`}
                    aria-label={`Verification badge for ${detail.label}`}
                    sx={ClaimTrackingStyles.verificationBadge}
                  >
                    {detail.value}
                  </Box>
                ) : (
                  <Typography 
                    id={`claim-tracking-detail-value-${index}`}
                    data-test-id={`test-claim-tracking-detail-value-${index}`}
                    aria-label={`${detail.label} value`}
                    sx={ClaimTrackingStyles.detailValue}
                  >
                    {detail.value}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          <Box 
            id="claim-tracking-divider"
            data-test-id="test-claim-tracking-divider"
            aria-label="Claim tracking section divider"
            sx={{ height: '2px', background: 'var(--primary-blue)', marginTop: '24px' }} 
          />
        </Box>
      </Box>

      {/* Timeline Section */}
      <Box 
        id="claim-tracking-timeline-section"
        data-test-id="test-claim-tracking-timeline-section"
        aria-label="Claim timeline section"
        sx={ClaimTrackingStyles.timelineSection}
      >
        <Typography 
          id="claim-tracking-timeline-title"
          data-test-id="test-claim-tracking-timeline-title"
          aria-label="Claim timeline title"
          sx={ClaimTrackingStyles.timelineTitle}
        >
          {t("Claim Timeline")}
        </Typography>
        <BasicTimeline
          data={mappedTimelineData}
          type="secondary"
          dataPosition={0}
        />
      </Box>
    </Box>
  );
};

export default ClaimTracking;
